import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { ApplicationSdk } from '../../core/sdk';
import { SEARCH_DELAY } from '../../core/constants';

export interface PaginationData {
    page: number;
    total: number;
    pageSize: number;
}

export interface ApiResponse {
    status: number;
    json: {
        data: any[];
        pagination: PaginationData;
        [key: string]: any;
    };
}

export interface BaseStoreState {
    sortOrder: number;
    sortField: string;
    searchValue: string;
    [key: string]: any;
}

/**
 * Abstract base class for overview stores with common functionality
 */
abstract class BaseOverviewStore<T = any> {
    protected sdk: ApplicationSdk;

    protected storageKey?(): string; // Storage could be optional
    protected abstract defaultSortField(): string;

    // Common observable properties
    public listLoading: boolean = false;
    public list: Array<T> = [];

    public sortOrder: number = 1;
    public sortField: string = '';
    public searchValue: string = '';
    public currentPage: number = 1;
    public maxPage: number = 1;
    public totalCount: number = 0;
    public latestPage: number = 1;
    public timeout: any = null;

    constructor(sdk: ApplicationSdk) {
        this.sdk = sdk;
        this.sortField = this.defaultSortField();

        // Make base properties observable
        makeObservable(this, {
            listLoading: observable,
            list: observable,
            sortOrder: observable,
            sortField: observable,
            searchValue: observable,
            currentPage: observable,
            maxPage: observable,
            totalCount: observable,
            updateSearch: action,
            clearSearch: action,
            onSort: action,
            onPageChange: action,
            fetchList: action,
            allItems: computed,
        });

        this.loadFromLocalStorage();
    }

    // Abstract methods that must be implemented by subclasses
    protected abstract getApiEndpoint(): string;
    protected abstract getApiPath(): string;
    protected abstract buildApiParams(page: number): any;
    protected abstract processApiResponse(response: ApiResponse): T[];
    protected getStorageState?(): BaseStoreState;
    protected applyStorageState?(state: BaseStoreState): void;

    // Common localStorage methods
    protected saveToLocalStorage(): void {
        if (!this.storageKey?.() || !this.getStorageState) return;
        const state = this.getStorageState();
        localStorage.setItem(this.storageKey(), JSON.stringify(state));
    }

    protected loadFromLocalStorage(): void {
        if (!this.storageKey?.() || !this.applyStorageState) return;
        const savedState = localStorage.getItem(this.storageKey());
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                runInAction(() => {
                    this.applyStorageState!(state);
                });
            } catch (error) {
                console.error(
                    `Failed to load state from localStorage for ${this.storageKey}:`,
                    error
                );
            }
        }
    }

    public onSearch(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        this.fetchList();
    }
    // Common search functionality
    public updateSearch(value: string): void {
        this.searchValue = value;
        this.saveToLocalStorage();

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        if (SEARCH_DELAY) {
            this.timeout = setTimeout(() => {
                this.fetchList();
            }, SEARCH_DELAY);
        }
    }

    public clearSearch(): void {
        if (this.searchValue) {
            this.searchValue = '';
            this.saveToLocalStorage();
            this.fetchList();
        }
    }

    // Common sorting functionality
    public async onSort(sortField: string): Promise<void> {
        runInAction(() => {
            if (this.sortField === sortField) {
                this.sortOrder = this.sortOrder === 1 ? -1 : 1;
            } else {
                this.sortOrder = -1;
            }
            this.sortField = sortField;
            this.saveToLocalStorage();
        });

        await this.fetchList();
    }

    // Common pagination functionality
    public onPageChange(nextPage: number): void {
        this.fetchList(nextPage);
    }

    // Common API fetch functionality
    public async fetchList(page: number = 1): Promise<void> {
        this.latestPage = page;

        runInAction(() => {
            this.listLoading = true;
        });

        try {
            const apiParams = this.buildApiParams(page);
            const response: ApiResponse = await this.sdk.fetchApi(
                this.getApiEndpoint(),
                this.getApiPath(),
                apiParams
            );

            runInAction(() => {
                this.listLoading = false;

                if (response?.status === 200) {
                    this.list = this.processApiResponse(response);
                    this.currentPage = response.json.pagination.page;
                    this.totalCount = response.json.pagination.total;
                    this.maxPage = Math.ceil(this.totalCount / response.json.pagination.pageSize);
                }
            });
        } catch (error) {
            runInAction(() => {
                this.listLoading = false;
            });
            console.error(`Failed to fetch ${this.getApiEndpoint()} list:`, error);
        }
    }

    // Common initialization
    public async init(): Promise<void> {
        await this.fetchList();
    }

    // Common dialog handling
    public async onDialogLeave(dialog: string, event: any = null): Promise<void> {
        if (event?.event === 'SAVED') {
            await this.fetchList(this.latestPage);
        }
    }

    // Common computed property
    get allItems(): Array<T> | null {
        if (!this.list) return null;
        return this.list;
    }

    // Common cleanup
    public onLeaveStore(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
}

export default BaseOverviewStore;
